import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { format, isToday, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Plus,
  Search,
  Calendar,
  Clock,
  RefreshCw,
  User,
  Play,
  CheckCircle,
  XCircle,
  AlertCircle,
  Stethoscope,
  FileText,
} from 'lucide-react';
import { encountersApi } from '@/api/endpoints/clinical.api';
import { EncounterStatus, EncounterType, EncounterStatusLabels, EncounterTypeLabels } from '@/types/clinical.types';

export default function EncountersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || 'all');
  const [typeFilter, setTypeFilter] = useState<string>(searchParams.get('type') || 'all');
  const [viewMode, setViewMode] = useState<'today' | 'all'>('today');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;

  // Query: Today's encounters (frequently updated)
  const { data: todayEncounters = [] } = useQuery({
    queryKey: ['encounters', 'today'],
    queryFn: () => encountersApi.getToday(),
    staleTime: 1000 * 60 * 1, // 1 minute - changes frequently
    refetchInterval: 1000 * 60 * 2, // Auto-refresh every 2 minutes
  });

  // Query: In-progress encounters (frequently updated)
  const { data: inProgressEncounters = [] } = useQuery({
    queryKey: ['encounters', 'in-progress'],
    queryFn: () => encountersApi.getInProgress(),
    staleTime: 1000 * 30, // 30 seconds - critical real-time info
    refetchInterval: 1000 * 60, // Auto-refresh every minute
  });

  // Query: All encounters with filters
  const {
    data: encountersData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['encounters', 'all', { page: currentPage, status: statusFilter, type: typeFilter }],
    queryFn: () =>
      encountersApi.getAll({
        page: currentPage,
        size: pageSize,
        status: statusFilter !== 'all' ? (statusFilter as EncounterStatus) : undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
      }),
    staleTime: 1000 * 60 * 2, // 2 minutes
    placeholderData: keepPreviousData,
    enabled: viewMode === 'all', // Only fetch when viewing all
  });

  const encounters = encountersData?.content || [];
  const totalPages = encountersData?.totalPages || 0;
  const totalElements = encountersData?.totalElements || 0;

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['encounters'] });
  };

  const handleStartEncounter = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await encountersApi.start(id);
      queryClient.invalidateQueries({ queryKey: ['encounters'] });
    } catch (err) {
      console.error('Erro ao iniciar atendimento:', err);
    }
  };

  const handleCompleteEncounter = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await encountersApi.complete(id);
      queryClient.invalidateQueries({ queryKey: ['encounters'] });
    } catch (err) {
      console.error('Erro ao finalizar atendimento:', err);
    }
  };

  const getStatusIcon = (status: EncounterStatus) => {
    switch (status) {
      case EncounterStatus.SCHEDULED:
        return <Calendar className="h-4 w-4 text-blue-500" />;
      case EncounterStatus.IN_PROGRESS:
        return <Play className="h-4 w-4 text-yellow-500" />;
      case EncounterStatus.COMPLETED:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case EncounterStatus.CANCELLED:
        return <XCircle className="h-4 w-4 text-red-500" />;
      case EncounterStatus.NO_SHOW:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Calendar className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadgeColor = (status: EncounterStatus) => {
    switch (status) {
      case EncounterStatus.SCHEDULED:
        return 'bg-blue-100 text-blue-800';
      case EncounterStatus.IN_PROGRESS:
        return 'bg-yellow-100 text-yellow-800';
      case EncounterStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      case EncounterStatus.CANCELLED:
        return 'bg-red-100 text-red-800';
      case EncounterStatus.NO_SHOW:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeBadgeColor = (type: EncounterType) => {
    switch (type) {
      case EncounterType.CONSULTATION:
        return 'bg-purple-100 text-purple-800';
      case EncounterType.FOLLOW_UP:
        return 'bg-indigo-100 text-indigo-800';
      case EncounterType.URGENT:
        return 'bg-red-100 text-red-800';
      case EncounterType.TELEHEALTH:
        return 'bg-cyan-100 text-cyan-800';
      case EncounterType.HOME_VISIT:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatScheduledTime = (dateStr?: string) => {
    if (!dateStr) return 'Sem horário';
    const date = parseISO(dateStr);
    if (isToday(date)) {
      return format(date, "'Hoje às' HH:mm", { locale: ptBR });
    }
    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const filteredEncounters = (viewMode === 'today' ? todayEncounters : encounters).filter((encounter) => {
    const matchesSearch = search === '' ||
      encounter.patientName.toLowerCase().includes(search.toLowerCase()) ||
      (encounter.providerName || '').toLowerCase().includes(search.toLowerCase()) ||
      (encounter.reason || '').toLowerCase().includes(search.toLowerCase());

    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Atendimentos</h1>
          <p className="text-muted-foreground">
            Gerencie consultas, retornos e atendimentos
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 rounded-lg border px-4 py-2 hover:bg-muted"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          <button
            onClick={() => navigate('/encounters/new')}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Novo Atendimento
          </button>
        </div>
      </div>

      {/* In Progress Section */}
      {inProgressEncounters.length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Play className="h-5 w-5 text-yellow-600" />
            <h2 className="font-semibold text-yellow-800">
              Em Atendimento ({inProgressEncounters.length})
            </h2>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {inProgressEncounters.map((encounter) => (
              <button
                key={encounter.id}
                onClick={() => navigate(`/encounters/${encounter.id}`)}
                className="flex items-center gap-3 rounded-lg bg-white p-3 text-left shadow-sm transition hover:shadow-md"
              >
                <Stethoscope className="h-5 w-5 text-yellow-500" />
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{encounter.patientName}</p>
                  <p className="truncate text-sm text-muted-foreground">
                    {encounter.typeLabel || EncounterTypeLabels[encounter.type]}
                    {encounter.startedAt && ` - Iniciado ${format(parseISO(encounter.startedAt), "HH:mm", { locale: ptBR })}`}
                  </p>
                </div>
                <button
                  onClick={(e) => handleCompleteEncounter(encounter.id, e)}
                  className="rounded-lg bg-green-100 p-2 text-green-700 hover:bg-green-200"
                  title="Finalizar atendimento"
                >
                  <CheckCircle className="h-4 w-4" />
                </button>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="flex items-center gap-4">
        <div className="flex rounded-lg border">
          <button
            onClick={() => setViewMode('today')}
            className={`px-4 py-2 text-sm font-medium transition ${
              viewMode === 'today'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted'
            }`}
          >
            Agenda do Dia ({todayEncounters.length})
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`px-4 py-2 text-sm font-medium transition ${
              viewMode === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted'
            }`}
          >
            Todos os Atendimentos
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por paciente, profissional ou motivo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border bg-background py-2 pl-10 pr-4"
          />
        </div>
        {viewMode === 'all' && (
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(0);
              }}
              className="rounded-lg border bg-background px-3 py-2"
            >
              <option value="all">Todos os Status</option>
              {Object.entries(EncounterStatusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentPage(0);
              }}
              className="rounded-lg border bg-background px-3 py-2"
            >
              <option value="all">Todos os Tipos</option>
              {Object.entries(EncounterTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">Erro ao carregar atendimentos. Tente novamente.</p>
          <button
            onClick={() => refetch()}
            className="mt-2 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && viewMode === 'all' ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          {/* Encounters List */}
          {filteredEncounters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">
                {viewMode === 'today' ? 'Nenhum atendimento hoje' : 'Nenhum atendimento encontrado'}
              </h3>
              <p className="mt-2 text-muted-foreground">
                {search || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'Tente ajustar os filtros de busca'
                  : 'Clique em "Novo Atendimento" para agendar'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredEncounters.map((encounter) => (
                <div
                  key={encounter.id}
                  onClick={() => navigate(`/encounters/${encounter.id}`)}
                  className="cursor-pointer rounded-lg border bg-card p-4 transition hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      {getStatusIcon(encounter.status)}
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{encounter.patientName}</h3>
                          <span className={`rounded-full px-2 py-0.5 text-xs ${getStatusBadgeColor(encounter.status)}`}>
                            {encounter.statusLabel || EncounterStatusLabels[encounter.status]}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-xs ${getTypeBadgeColor(encounter.type)}`}>
                            {encounter.typeLabel || EncounterTypeLabels[encounter.type]}
                          </span>
                        </div>
                        {encounter.reason && (
                          <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">
                            {encounter.reason}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col items-end gap-1 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatScheduledTime(encounter.scheduledAt)}
                        </div>
                        {encounter.providerName && (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {encounter.providerName}
                          </div>
                        )}
                        {encounter.notesCount > 0 && (
                          <div className="flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            {encounter.notesCount} evolução(ões)
                          </div>
                        )}
                      </div>
                      {encounter.status === EncounterStatus.SCHEDULED && (
                        <button
                          onClick={(e) => handleStartEncounter(encounter.id, e)}
                          className="rounded-lg bg-primary p-2 text-primary-foreground hover:bg-primary/90"
                          title="Iniciar atendimento"
                        >
                          <Play className="h-4 w-4" />
                        </button>
                      )}
                      {encounter.status === EncounterStatus.IN_PROGRESS && (
                        <button
                          onClick={(e) => handleCompleteEncounter(encounter.id, e)}
                          className="rounded-lg bg-green-600 p-2 text-white hover:bg-green-700"
                          title="Finalizar atendimento"
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {viewMode === 'all' && totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Mostrando {filteredEncounters.length} de {totalElements} registros
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage((prev) => prev - 1)}
                  disabled={currentPage === 0}
                  className="rounded-lg border px-3 py-1 disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="px-3 py-1">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="rounded-lg border px-3 py-1 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
